// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import { xIsPLPTableView } from "@awc/App/Store/Selectors/GeneralSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC, useState } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    view: state.pages.productList.view,
    // XNG-Changes
    showTableLayout: xIsPLPTableView(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    // XNG-Changes
    accordionFilterStyles?: AccordionSectionPresentationProps;
}

export const columnsStyles: ProductListColumnsStyles = {
    container: {
        gap: 20,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    leftColumnTableViewGridItem: {
        width: [12, 12, 0, 0, 0],
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
    },
    rightColumnTableViewGridItem: {
        width: 12,
    },
};

const styles = columnsStyles;
const Aside = getStyledWrapper("aside");
const Section = getStyledWrapper("section");

const ProductListColumns: FC<Props> = ({ id, view, showTableLayout }) => {
    // XNG-Changes
    const [toggled, setToggled] = useState(false);

    // XNG-Changes: use showTableLayout to check
    const leftColumnGridItemStyle = showTableLayout ? styles.leftColumnTableViewGridItem : styles.leftColumnGridItem;
    const rightColumnGridItemStyle = showTableLayout ? styles.rightColumnTableViewGridItem : styles.rightColumnGridItem;

    // XNG-Changes: use StyledWrapper instead of grid
    return (
        <StyledWrapper {...styles.container} {...{ showTableLayout }}>
            {showTableLayout ? (
                <Section {...leftColumnGridItemStyle}>
                    <Accordion headingLevel={2}>
                        <ManagedAccordionSection
                            title="Filter"
                            onTogglePanel={open => setToggled(open)}
                            {...styles.accordionFilterStyles}
                        >
                            <Zone contentId={id} zoneName="Content02" />
                            <Zone contentId={id} zoneName="Content03" />
                        </ManagedAccordionSection>
                    </Accordion>
                </Section>
            ) : (
                <Aside {...leftColumnGridItemStyle} aria-label="Filters">
                    <Zone contentId={id} zoneName="Content00" />
                </Aside>
            )}
            <Section {...rightColumnGridItemStyle}>
                <Zone contentId={id} zoneName="Content01" />
            </Section>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        // XNG-Changes: add CategoryDetailsPage
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
